import axios from '@axios';
import ability from '@/libs/acl/ability'
import {SLOT_TYPES, SOURCES} from '@/config/layoutConfig';
import {getGridItemsForLayout, mapSlotsToNewLayout, regenerateLayoutIds} from '@/utils/gridUtils';
import AlarmViewService from './services/AlarmViewService';
import VideoViewService from './services/VideoViewService';
import StatisticsViewService from './services/StatisticsViewService';
import MaintenanceViewService from './services/MaintenanceViewService';

const state = {
  slotData: null,
  slotSelection: false,
  selectedAlarm: null,
  isConfigLayout: false,
  tmpLayoutItems: [],
  layoutModal: {
    data: {},
    source: ''
  },
  currentGridItems: [],
};

const getters = {
  slotDataWillOpenATab(state) {
    if (
      state.slotData &&
      state.slotData.source === SOURCES.SUBSYSTEM_TREE_DND
    ) {
      return true;
    }
    return false;
  },
  currentGridItems(state) {
    return state.currentGridItems;
  },
};

const mutations = {
  setSlotData(state, value) {
    state.slotData = value;
  },
  setSlotSelection(state, value) {
    state.slotSelection = value
  },
  setLayoutSlot(state, value) {
    state.tabs[state.activeTab].slots.push(value);
  },
  setSelectedAlarm(state, value) {
    state.selectedAlarm = value;
  },
  unsetLayoutSlot(state, value) {
    const i = state.tabs[state.activeTab].slots.map(item => item.pos).indexOf(value);
    if (i >= 0) {
      state.tabs[state.activeTab].slots.splice(i, 1);
    }
  },
  setTmpLayoutItem(state, value) {
    state.tmpLayoutItems.push(value);
  },
  removeTmpLayoutItem(state, value) {
    const i = state.tmpLayoutItems.map(item => item.pos).indexOf(value);
    if (i >= 0) {
      state.tmpLayoutItems.splice(i, 1);
    }
  },
  emptyTmpLayoutItems(state) {
    state.tmpLayoutItems = [];
  },
  updateLayoutSlots(state) {
    state.tabs[state.activeTab].slots = [];
    state.tabs[state.activeTab].slots = [...state.tmpLayoutItems];
  },
  forceLayoutSlots(state, value) {
    state.tabs[state.activeTab].slots = [];
    state.tabs[state.activeTab].slots = [...value];
  },
  setCurrentLayoutToTmp(state) {
    state.tmpLayoutItems = [...state.tabs[state.activeTab].slots];
  },
  showLayoutModal(state, value) {
    state.layoutModal = value;
  },
  resetLayoutModal(state) {
    state.layoutModal = {
      data: {},
      source: ''
    };
  },
  updateSlotData(state, value) {
    // Resolve the target tab (defaults to the active one for legacy callers)
    const tabId = value.tabId || state.activeTab;
    const tab = state.tabs[tabId];
    if (!tab || !Array.isArray(tab.slots)) return;
    // Match by id first (stable across reorders), fall back to pos
    const slot = tab.slots.find(s =>
      (value.id != null && s.id === value.id) ||
      (value.pos != null && s.pos === value.pos)
    );
    if (!slot) return;
    slot.data = Object.assign({}, slot.data, value.data)
  },
  updateTabSlots(state, {tabId, slots}) {
    if (state.tabs[tabId]) {
      state.tabs[tabId].slots = slots;
    }
  },
  updateTabHiddenSlots(state, {tabId, hiddenSlots}) {
    if (state.tabs[tabId]) {
      state.tabs[tabId].hiddenSlots = hiddenSlots;
    }
  },
};

const actions = {
  setSlotData({commit}, data) {
    commit('setSlotData', data);
    commit('setSlotSelection', true);
  },
  resetSlotSelection({commit}, data) {
    commit('setSlotData', null);
    commit('setSlotSelection', false);
  },
  addItemToLayout({commit, dispatch}, data) {
    commit('setLayoutSlot', data);
    dispatch('updateUserConfig');
  },
  removeItemFromLayout({commit, dispatch}, data) {
    commit('unsetLayoutSlot', data);
    dispatch('updateUserConfig');
  },
  addTmpLayoutItem({state, commit}, data) {
    const item = state.tmpLayoutItems.find(item => item.pos === data.pos);
    commit('removeTmpLayoutItem', data.pos);
    commit('setTmpLayoutItem', data);
  },
  removeTmpLayoutItem({commit}, data) {
    commit('removeTmpLayoutItem', data);
  },
  setSelectedAlarm({commit}, data) {
    commit('setSelectedAlarm', data);
  },
  unsetSelectedAlarm({commit}, data) {
    commit('setSelectedAlarm', null);
  },
  emptyTmpLayoutItems({commit}) {
    commit('emptyTmpLayoutItems');
  },
  setCurrentLayoutToTmp({commit}) {
    commit('setCurrentLayoutToTmp');
  },
  updateLayoutSlots({commit, dispatch}) {
    commit('updateLayoutSlots');
    dispatch('updateUserConfig');
  },
  showLayoutModal({commit}, data) {
    commit('showLayoutModal', data);
  },
  resetLayoutModal({commit}) {
    commit('resetLayoutModal');
  },
  updateSlotData({commit, dispatch}, data) {
    commit('updateSlotData', data);
  },

  async setAlarmView({commit, dispatch}, payload) {
    const alarmViewService = new AlarmViewService(this);
    return alarmViewService.setAlarmView(payload);
  },

  async setDefaultAlarmView({commit, dispatch}, alarm) {
    const alarmViewService = new AlarmViewService(this);
    return alarmViewService.setDefaultAlarmView(alarm);
  },

  async setUnitLiveVideo({commit, dispatch}, data) {
    const videoViewService = new VideoViewService(this);
    return videoViewService.setUnitLiveVideo(data);
  },

  async setUnitRecordingVideo({commit, dispatch}, data) {
    const videoViewService = new VideoViewService(this);
    return videoViewService.setUnitRecordingVideo(data);
  },

  async setUnitCamerasView({commit, dispatch}, data) {
    const videoViewService = new VideoViewService(this);
    return videoViewService.setUnitCamerasView(data);
  },

  async setStaticsView({commit, dispatch}, data) {
    const statisticsViewService = new StatisticsViewService(this);
    return statisticsViewService.setStaticsView(data);
  },

  async setMaintenanceView({commit, dispatch}, data) {
    const maintenanceViewService = new MaintenanceViewService(this);
    return maintenanceViewService.setMaintenanceView(data);
  },

  //we load the view from the query param using dispatch and load the necesary data
  async loadViewFromQuery({dispatch, state}, {unitId, viewType, viewId}) {
    if (!viewType) return;
    if (viewType === 'layout' && viewId) {
      await dispatch('setLayout', viewId);
      return;
    }
    if (!unitId) return;
    const numericUnitId = Number(unitId);
    const unitFromStore = (state.units || []).find(
      (u) => u.id === numericUnitId || u.id === unitId || String(u.id) === String(unitId)
    );
    let unitData = unitFromStore || null;
    let cameras = Array.isArray(unitFromStore?.cameras) ? unitFromStore.cameras : [];
    if (!unitData) {
      const rawUnit = await dispatch('unit/get', unitId, {root: true});
      if (!rawUnit) return;
      unitData = rawUnit?.data ?? rawUnit;
    }
    if (viewType === 'unit-live') {
      await dispatch('setUnitLiveVideo', {
        data: {data: unitData, cameras},
        source: SOURCES.SUBSYSTEM_TREE,
      });
    }
    if (viewType === 'unit-recording') {
      await dispatch('setUnitRecordingVideo', {
        data: {data: unitData, cameras},
        source: SOURCES.SUBSYSTEM_TREE,
      });
    }
  },

  setLayout({commit, state, dispatch, rootGetters}, layoutId) {
    const activeTabId = state.activeTab;
    const currentTab = state.tabs[activeTabId];

    const options = {
      slots: currentTab.slots || [],
      rootGetters
    };

    let gridItems = getGridItemsForLayout(layoutId, options);
    // Regenerate ids so this tab's widgets never collide with another tab that
    // uses the same layout template.
    gridItems = regenerateLayoutIds(gridItems);

    commit('updateTabLayout', {tabId: activeTabId, layoutId});
    commit('updateTabLayoutGrid', {tabId: activeTabId, gridItems});

    const currentSlots = currentTab.slots || [];
    const currentHiddenSlots = currentTab.hiddenSlots || [];
    const hasActiveWidgets = currentSlots.some(slot => slot.type) || currentHiddenSlots.length > 0;

    if (hasActiveWidgets) {
      const {mappedSlots, unmappedWidgets} = mapSlotsToNewLayout(
        currentSlots,
        gridItems,
        currentHiddenSlots
      );

      commit('updateTabSlots', {tabId: activeTabId, slots: mappedSlots});
      commit('updateTabHiddenSlots', {tabId: activeTabId, hiddenSlots: unmappedWidgets});
    }

    dispatch('updateUserConfig');
    return {status: true};
  },
  addAlarmInfoToAvailableSlot({state, commit, dispatch, rootGetters}, alarmData) {
    console.log('addAlarmInfoToAvailableSlot');
    try {
      // Obtener el tab actual
      const currentTab = state.tabs[state.activeTab];
      if (!currentTab) {
        console.log('No hay tab activo');
        return null;
      }

      // Obtener gridItems y slots del tab actual
      const gridItems = currentTab.gridItems || [];
      const slots = currentTab.slots || [];

      // Encontrar el primer gridItem que no tenga un slot asignado
      const availableGridItem = gridItems.find(gridItem => {
        // Verificar si ya existe un slot para esta posición
        const hasSlot = slots.some(slot => slot.id === gridItem.id || slot.pos === gridItem.pos);
        return !hasSlot;
      });

      if (!availableGridItem) {
        console.log('No hay slots disponibles en el layout actual');
        return null;
      }

      // Crear el slot de información con los datos de la alarma
      console.log('alarmData',alarmData);
      const infoSlot = {
        ...availableGridItem,
        name: `Alarm ${alarmData.id}`,
        type: SLOT_TYPES.INFO,
        type2: SLOT_TYPES.INFO,
        data: {
          ...alarmData,
          in_app_source: SOURCES.ALARM
        }
      };

      // Agregar el slot al layout
      commit('setLayoutSlot', infoSlot);
      dispatch('updateUserConfig');
      console.log('InfoSlot agregado en posición:', availableGridItem.pos);
      return infoSlot;

    } catch (error) {
      console.log('Error:', error);
      return null;
    }

  },
  async setMapView({commit, dispatch}, data) {
    const slots = (data) => {
      const slots = [];

      slots.push({
        name: 'Unit map',
        pos: 1,
        data: {
          unit_id: data.data.id
        },
        type: SLOT_TYPES.UNITS_MAP
      });

      return slots;
    }

    const tabName = `Mapa ${data.data.description}`;
    const tab = await this.dispatch("grid/addNewTab", {tabName, layout: 'null'});

    if (tab.status) {
      await this.dispatch("grid/setActiveTab", tab.id);
      this.commit("grid/updateTabLayout", { tabId: tab.id, layoutId: 'unit-map' });
      this.commit("grid/forceLayoutSlots", slots(data));
    }

  },
  async setAlarmDmpLogsView({commit, dispatch}, data) {
    const alarm = data?.alarm;
    if (!alarm?.id) return;

    let idDmpPanel = null;
    try {
      const response = await this.dispatch('dmp_alarms/getAll', { id_alarm: alarm.id, q: '' });
      idDmpPanel = response?.data?.[0]?.id_dmp_panel ?? null;
    } catch (error) {
      idDmpPanel = null;
    }

    const unitName = alarm?.unit?.name || alarm?.unit?.description || '';
    const tabName = `Logs panel ${unitName}`.trim();

    const slots = [{
      name: 'DMP logs',
      pos: 1,
      type: SLOT_TYPES.DRAGGABLE_MENU,
      id: 'alarm-dmp-logs-1',
      data: {
        type: 'dmp_logs',
        filters: {
          source: 'dmp_logs',
          filters: { panelFilter: idDmpPanel },
        },
      },
    }];

    const tab = await this.dispatch("grid/addNewTab", {
      tabName,
      layout: 'alarm-dmp-logs',
      slots,
    });

    if (tab.status) {
      await this.dispatch("grid/setActiveTab", tab.id);
      this.commit("grid/forceLayoutSlots", slots);
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions
};
